// apps/web/src/components/painel/home/assinatura-po-estoque.tsx — Fase 17.8, Plano 17 (D-12; UI-SPEC U-19, U-24).
// Assinatura "Pó em estoque · kg · traço = mínimo" do Almoxarifado: até 6 cores (o botão "Ver todas" expande no próprio painel até o
// que a API devolveu), cada uma com a amostra da cor RAL (48px), um trilho de 4px com o nível do saldo e o traço do mínimo, o código
// e o saldo em kg. Abaixo do mínimo: trilho e saldo em --color-warning, ícone de alerta e o texto oculto "abaixo do mínimo" (a cor
// nunca é o único sinal). A API manda só o texto cadastrado da cor; o hex vem do mapa de dados RAL_HEX por `normalizarCor`.
import { useId, useState, type ReactElement } from 'react';
import { AlertTriangle, Boxes } from 'lucide-react';
import type { CorPoEstoque } from '@pieltec/contracts';
import { formatarNumero } from '../../../lib/painel/home/formato';
import { codigoDaCor, nomeDaCor, normalizarCor } from '../../../lib/painel/home/ral-hex';
import { EstadoVazio } from './estado-vazio';
import '../../../styles/painel-home.css';

export const TEXTO_SEM_PO_COM_SALDO = 'Nenhum pó com saldo cadastrado.';

/** Células visíveis antes de "Ver todas". */
export const CELULAS_VISIVEIS_PO = 6;

export interface AssinaturaPoEstoqueProps {
  readonly cores: readonly CorPoEstoque[];
  /** Todas as cores de pó com saldo ou com mínimo (pode ser maior que `cores.length`). */
  readonly totalCores: number;
}

export interface AssinaturaPoEstoqueVisaoProps extends AssinaturaPoEstoqueProps {
  readonly expandido: boolean;
  readonly onAlternar: () => void;
}

/** Fração do trilho preenchida (0 a 1, três casas): saldo sobre a maior referência da lista; referência inválida não preenche. */
export function nivelDoTrilho(saldoKg: number, referenciaKg: number): number {
  if (!Number.isFinite(saldoKg) || !Number.isFinite(referenciaKg) || referenciaKg <= 0 || saldoKg <= 0) return 0;
  return Math.min(1, Math.round((saldoKg / referenciaKg) * 1000) / 1000);
}

/** Posição do traço do mínimo em % (1 casa) do trilho; sem mínimo (ou referência inválida) não há traço. */
export function posicaoDoMinimo(minimoKg: number | null, referenciaKg: number): number | null {
  if (minimoKg === null || !Number.isFinite(minimoKg) || !Number.isFinite(referenciaKg) || referenciaKg <= 0) return null;
  return Math.min(100, Math.round((minimoKg / referenciaKg) * 1000) / 10);
}

/** "62 kg", "9,5 kg", "1.250 kg": uma casa só quando o saldo é fracionado. */
function kg(valor: number): string {
  return `${formatarNumero(valor, Number.isInteger(valor) ? 0 : 1)} kg`;
}

export interface AmostraDaCor {
  readonly classe: string;
  readonly hex?: string;
  readonly codigo: string;
  readonly nome: string;
}

export function amostraDaCor(corCodigo: string): AmostraDaCor {
  const cor = normalizarCor(corCodigo);
  if (cor === null) return { classe: 'painel-amostra painel-po__amostra painel-amostra--neutra', codigo: corCodigo, nome: corCodigo };
  if (cor.tipo === 'ral') return { classe: 'painel-amostra painel-po__amostra', hex: cor.hex, codigo: codigoDaCor(cor), nome: nomeDaCor(cor) };
  // PTFE e cor fora do mapa: cinza neutro com o código escrito (a cor exata é a do cadastro).
  return { classe: 'painel-amostra painel-po__amostra painel-amostra--neutra', codigo: codigoDaCor(cor), nome: nomeDaCor(cor) };
}

function Celula({ cor, referenciaKg }: { readonly cor: CorPoEstoque; readonly referenciaKg: number }): ReactElement {
  const amostra = amostraDaCor(cor.corCodigo);
  const posicao = posicaoDoMinimo(cor.minimoKg, referenciaKg);
  return (
    <li className="painel-po" data-testid="painel-po-cor" data-abaixo={cor.abaixoDoMinimo ? 'true' : 'false'}>
      <span className={amostra.classe} style={amostra.hex === undefined ? undefined : { background: amostra.hex }} aria-hidden="true" />
      <span className="painel-po__trilho" aria-hidden="true">
        <span className={cor.abaixoDoMinimo ? 'painel-po__nivel painel-po__nivel--abaixo' : 'painel-po__nivel'} style={{ transform: `scaleX(${nivelDoTrilho(cor.saldoKg, referenciaKg)})` }} />
        {posicao !== null && <span className="painel-po__minimo" style={{ left: `${posicao}%` }} />}
      </span>
      <span className="painel-po__codigo">{amostra.codigo}</span>
      <span className="painel-po__saldo">
        <span className="sr-only">{`${amostra.nome}: `}</span>
        {kg(cor.saldoKg)}
        {cor.abaixoDoMinimo && <AlertTriangle className="painel-po__alerta size-3" aria-hidden="true" />}
        {cor.abaixoDoMinimo && <span className="sr-only">abaixo do mínimo</span>}
        {cor.minimoKg !== null && <span className="sr-only">{`, mínimo ${kg(cor.minimoKg)}`}</span>}
      </span>
    </li>
  );
}

/** Visão controlada (testável por SSR): `AssinaturaPoEstoque` só guarda expandido/recolhido. */
export function AssinaturaPoEstoqueVisao({ cores, totalCores, expandido, onAlternar }: AssinaturaPoEstoqueVisaoProps): ReactElement {
  const idTitulo = useId();
  const idLista = useId();
  const visiveis = expandido ? cores : cores.slice(0, CELULAS_VISIVEIS_PO);
  // A referência do trilho é a maior entre saldo e mínimo de TODAS as cores recebidas, para que expandir não mude o nível das primeiras.
  const referenciaKg = Math.max(0, ...cores.map((cor) => Math.max(cor.saldoKg, cor.minimoKg ?? 0)));
  const temMais = totalCores > CELULAS_VISIVEIS_PO || cores.length > CELULAS_VISIVEIS_PO;
  return (
    <section className="painel-assinatura" data-testid="painel-assinatura" data-tipo="po-estoque" aria-labelledby={idTitulo}>
      <header className="painel-assinatura__cabecalho">
        <h3 id={idTitulo} className="painel-assinatura__titulo">
          Pó em estoque
        </h3>
        <span className="painel-assinatura__subtitulo">kg · traço = mínimo</span>
      </header>
      {cores.length === 0 ? (
        <EstadoVazio icone={Boxes} texto={TEXTO_SEM_PO_COM_SALDO} />
      ) : (
        <>
          <ul id={idLista} className="painel-assinatura__grade painel-po__grade">
            {visiveis.map((cor) => (
              <Celula key={cor.corCodigo} cor={cor} referenciaKg={referenciaKg} />
            ))}
          </ul>
          {temMais && (
            <button type="button" className="painel-em-poder__ver-todos" aria-expanded={expandido} aria-controls={idLista} onClick={onAlternar} data-testid="painel-po-ver-todas">
              {expandido ? 'Mostrar menos' : `Ver todas (${formatarNumero(Math.max(totalCores, cores.length))})`}
            </button>
          )}
          {expandido && totalCores > cores.length && (
            <p className="painel-em-poder__nota">{`Mostrando as ${formatarNumero(cores.length)} de menor cobertura de ${formatarNumero(totalCores)}.`}</p>
          )}
        </>
      )}
    </section>
  );
}

export function AssinaturaPoEstoque(props: AssinaturaPoEstoqueProps): ReactElement {
  const [expandido, setExpandido] = useState(false);
  return <AssinaturaPoEstoqueVisao {...props} expandido={expandido} onAlternar={() => setExpandido((atual) => !atual)} />;
}
